import { useEffect, useRef } from 'react'
import Cookie, { ICookie } from 'cookie-universal'
import { z } from 'zod'
import { useBucket } from '@mc/experiments-react'
import { trackExperimentViewed } from '@mc/track-event'

// Growthbook experiment: https://app.growthbook.io/features/com-1692-on-call-free-trial
export const COM_1692_ON_CALL_FREE_TRIAL_FLAG = 'com-1692-on-call-free-trial'

const FREE_TRIAL_COOKIE_NAME = 'free_trial_promo'
const FREE_TRIAL_MODAL_VIEWED_SESSION_KEY = 'free_trial_promo_modal_viewed'

const ONE_WEEK = 60 * 60 * 24 * 7

export type FreeTrialPromoData = z.infer<typeof FreeTrialPromoDataSchema>

export type FreeTrialPromo =
  | (FreeTrialPromoData & {
      showPromoInCheckout: boolean
      showPromoModal: boolean
    })
  | null

export const shouldShowFreeTrialPromo = ({
  isFreeTrialPromoEnabled,
  isPromoActive,
  hasPromoCookie,
}: {
  isFreeTrialPromoEnabled: boolean
  isPromoActive: boolean
  hasPromoCookie: boolean
}) => hasPromoCookie && isFreeTrialPromoEnabled && isPromoActive

export const shouldShowModal = ({
  instructorSlug,
  includeInstructorSlugs,
  isPromoId,
  isFreeTrialPromoEnabled,
}: {
  instructorSlug: string
  includeInstructorSlugs: string[]
  isPromoId: boolean
  isFreeTrialPromoEnabled: boolean
}) =>
  isPromoId &&
  includeInstructorSlugs.includes(instructorSlug) &&
  isFreeTrialPromoEnabled

export const setFreeTrialPromoAppliedCookie = () => {
  const cookies = Cookie()
  cookies.set(FREE_TRIAL_COOKIE_NAME, 'true', {
    maxAge: ONE_WEEK,
    path: '/',
  })
}

export const getFreeTrialPromoAppliedCookie = (cookies: ICookie) =>
  cookies.get(FREE_TRIAL_COOKIE_NAME)

export const removeFreeTrialPromoAppliedCookie = (cookiesObj?: ICookie) => {
  const cookies = cookiesObj || Cookie()
  cookies.remove(FREE_TRIAL_COOKIE_NAME)
}

export const setFreeTrialPromoModalViewed = () => {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(FREE_TRIAL_MODAL_VIEWED_SESSION_KEY, 'true')
  }
}

export const getFreeTrialPromoModalViewed = () => {
  if (typeof window === 'undefined') {
    return false
  }
  return sessionStorage.getItem(FREE_TRIAL_MODAL_VIEWED_SESSION_KEY) === 'true'
}

export const useIsFreeTrialPromoEnabled = ({
  shouldTrackExperimentViewed = false,
}: {
  shouldTrackExperimentViewed?: boolean
} = {}) => {
  const isExperimentTracked = useRef(false)
  const bucket = useBucket<'control' | 'variant_1'>(
    COM_1692_ON_CALL_FREE_TRIAL_FLAG,
  )

  useEffect(() => {
    if (bucket && !isExperimentTracked.current && shouldTrackExperimentViewed) {
      trackExperimentViewed(
        COM_1692_ON_CALL_FREE_TRIAL_FLAG,
        {},
        { trackUserLeap: true },
      )
      isExperimentTracked.current = true
    }
  }, [bucket, shouldTrackExperimentViewed])

  return bucket === 'variant_1'
}

const FreeTrialPromoDataSchema = z.object({
  promoId: z.string(),
  promoLengthInDays: z.number(),
  isActive: z.boolean(),
  termsLink: z.string().url(),
  instructorSlugs: z.array(z.string()),
  freeTrialModal: z.object({
    copy: z.object({
      title: z.string(),
      description: z.array(z.string()),
      cta: z.string(),
    }),
  }),
  offerEndedModal: z.object({
    copy: z.object({
      title: z.string(),
      description: z.string(),
      cta: z.string(),
    }),
  }),
  membershipPage: z.object({
    copy: z.object({
      benefits: z.object({
        access: z.string(),
        unlimited: z.string(),
        secure: z.string(),
      }),
      tiers: z.object({
        annual: z.array(z.string()),
        monthly: z.array(z.string()),
      }),
      ctaNote: z.array(z.string()),
    }),
  }),
  paymentPage: z.object({
    copy: z.object({
      terms: z.array(z.string()),
      summary: z.object({
        title: z.string(),
        chargedNow: z.object({
          title: z.string(),
          amount: z.string(),
          description: z.array(z.string()),
        }),
        firstCharged: z.string(),
        totalCharged: z.string(),
      }),
    }),
  }),
})

export const parseFreeTrialPromoData = (variables: Record<string, unknown>) => {
  if (!variables) return null
  const result = FreeTrialPromoDataSchema.safeParse(variables)
  if (!result.success) return null
  return result.data
}
